import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type PageChrome = {
  title: string;
  fill?: boolean;
  bleed?: boolean;
  /** Path the card header's back button navigates to. Serializable so overrides can carry it. */
  backTo?: string;
  /** One line under the card header title. */
  subtitle?: string;
};

type Entry = PageChrome & {
  path: string;
  resolve?: (params: Record<string, string>) => Partial<PageChrome>;
};

const CHANNEL_CHROME: Record<string, PageChrome> = {
  whatsapp: { title: 'WhatsApp', fill: true },
  instagram: { title: 'Instagram', fill: true },
  facebook: { title: 'Facebook', fill: true },
  'ai-agents': { title: 'AI Live Chat', fill: true },
};

const ROUTES: Entry[] = [
  { path: '/home', title: 'Dashboard', bleed: true },
  { path: '/home/V3/dashboard', title: 'Dashboard', bleed: true },
  { path: '/home/main-home', title: 'Dashboard', bleed: true },

  { path: '/home/channels', title: 'Inbox' },
  {
    path: '/home/channels/:channel',
    title: 'Inbox',
    fill: true,
    resolve: ({ channel }) => CHANNEL_CHROME[channel] ?? { title: 'Inbox' },
  },
  { path: '/home/inbox', title: 'Inbox', fill: true },
  { path: '/home/new-inbox', title: 'Inbox', fill: true },
  { path: '/home/social-inbox', title: 'Social inbox', fill: true },
  { path: '/home/live-chat', title: 'Live chat', fill: true },
  { path: '/home/team-chat', title: 'Team chat', fill: true },

  { path: '/home/campaigns', title: 'Campaigns', fill: true },
  { path: '/home/templates', title: 'Templates' },
  { path: '/home/instagram-broadcasts', title: 'Instagram broadcasts' },

  { path: '/home/automation', title: 'Automations', fill: true },
  { path: '/home/automation/flows', title: 'Automations', fill: true },
  { path: '/home/automation/flows/:id/executions', title: 'Automation runs', bleed: true },
  { path: '/home/automation/flows/:id/analytics', title: 'Automation runs', bleed: true },
  { path: '/home/automation/facebook/:id/analytics', title: 'Automation runs', bleed: true },
  { path: '/home/automation/runs', title: 'Automation runs' },
  { path: '/home/form-data', title: 'Forms' },
  { path: '/home/flows', title: 'Forms', bleed: true },

  { path: '/home/boards', title: 'CRM', fill: true },
  { path: '/home/boards/leads', title: 'CRM', fill: true },
  { path: '/home/boards/:id', title: 'CRM', fill: true },
  { path: '/home/tickets', title: 'Tickets', fill: true },
  { path: '/home/deals', title: 'Deals', fill: true },
  { path: '/home/contacts', title: 'Contacts' },
  { path: '/home/segments', title: 'Segments' },

  { path: '/home/social-media/posts', title: 'Social media posts', fill: true },
  { path: '/home/social-media/posts/create-global', title: 'Social media posts', fill: true },
  { path: '/home/scheduling-posts', title: 'Social posts' },
  { path: '/home/social-accounts', title: 'Social accounts' },

  { path: '/home/ads', title: 'Meta Ads', fill: true },
  // Every ads sub-route ends in an id — a campaign, ad set or ad — which
  // humanise() would print raw in the header. They all belong to Meta Ads,
  // and the page itself names the record it is showing.
  { path: '/home/ads/*', title: 'Meta Ads' },
  // The three records reached from the ads tables carry their own `p-6`, so the
  // shell's px-4/py-5 only doubled it and pushed the table in from the card
  // edge. They keep the shell's scrolling — only the padding goes.
  { path: '/home/ads/campaign/:metaCampaignId', title: 'Meta Ads', bleed: true },
  { path: '/home/ads/adset/:metaAdSetId', title: 'Meta Ads', bleed: true },
  { path: '/home/ads/meta/:metaAdId', title: 'Meta Ads', bleed: true },
  { path: '/home/analytics', title: 'Analytics' },
  { path: '/home/appointments', title: 'Appointments', bleed: true },
  { path: '/home/scheduling', title: 'Appointments' },
  { path: '/home/commerce/*', title: 'Commerce' },
  { path: '/home/commerce', title: 'Commerce', fill: true },
  { path: '/home/petpooja', title: 'POS', fill: true },
  { path: '/home/data-stores', title: 'Datastore', bleed: true },
  { path: '/home/data-stores/:id', title: 'Datastore', bleed: true },
  { path: '/home/integrations', title: 'Integrations', bleed: true },
  { path: '/home/ai-agents', title: 'AI Agents', bleed: true },
  { path: '/home/voice-agents', title: 'Voice agents' },
  { path: '/home/audit-log', title: 'Audit log', fill: true },
  { path: '/home/account-settings', title: 'Account settings', bleed: true },
  { path: '/home/workspace-settings', title: 'Workspace settings', bleed: true },
  { path: '/home/settings', title: 'Settings' },
  { path: '/home/team-management', title: 'Team management', bleed: true },
  { path: '/home/billing', title: 'Billing' },
  { path: '/home/notifications', title: 'Notifications' },
  { path: '/home/updates', title: "What's new in WeNext" },
];

type Match = { chrome: PageChrome; score: number };

const matchEntry = (entry: Entry, pathname: string): Match | null => {
  const want = entry.path.split('/').filter(Boolean);
  const got = pathname.split('/').filter(Boolean);
  const rest = want[want.length - 1] === '*';
  if (rest) want.pop();
  if (rest ? got.length < want.length : got.length !== want.length) return null;

  const params: Record<string, string> = {};
  let score = 0;
  for (let i = 0; i < want.length; i += 1) {
    const segment = want[i];
    if (segment.startsWith(':')) {
      params[segment.slice(1)] = got[i];
      score += 1; // a param segment is a weaker match than a literal one
      continue;
    }
    if (segment !== got[i]) return null;
    score += 2;
  }

  // A prefix entry describes everything under it, so it must lose to an entry
  // that spells the path out — otherwise '/home/commerce/*' shadows
  // '/home/commerce' and the page loses its own chrome flags.
  if (rest) score -= 1;

  const { path: _path, resolve, ...chrome } = entry;
  return { chrome: { ...chrome, ...resolve?.(params) }, score };
};

const humanise = (pathname: string): PageChrome => {
  const last = pathname.split('/').filter(Boolean).pop() ?? 'WeNext';
  const words = last.replace(/[-_]/g, ' ').trim();
  return { title: words.charAt(0).toUpperCase() + words.slice(1) };
};

export const resolveRouteChrome = (pathname: string): PageChrome => {
  let best: Match | null = null;
  for (const entry of ROUTES) {
    const match = matchEntry(entry, pathname);
    if (match && (!best || match.score > best.score)) best = match;
  }
  return best?.chrome ?? humanise(pathname);
};

type ChromeStore = {
  override: PageChrome | null;
  setOverride: (chrome: PageChrome | null) => void;
};

const PageChromeContext = createContext<ChromeStore | null>(null);

export const PageChromeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [override, setOverride] = useState<PageChrome | null>(null);
  const value = useMemo(() => ({ override, setOverride }), [override]);
  return <PageChromeContext.Provider value={value}>{children}</PageChromeContext.Provider>;
};

export const useResolvedPageChrome = (pathname: string): PageChrome => {
  const store = useContext(PageChromeContext);
  const routeChrome = useMemo(() => resolveRouteChrome(pathname), [pathname]);
  return store?.override ?? routeChrome;
};

export const usePageChrome = (chrome: PageChrome | null): void => {
  const store = useContext(PageChromeContext);
  const setOverride = store?.setOverride;
  const key = chrome ? JSON.stringify(chrome) : null;

  useEffect(() => {
    if (!setOverride) return;
    setOverride(key ? (JSON.parse(key) as PageChrome) : null);
    return () => setOverride(null);
  }, [key, setOverride]);
};
